import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { IoAddCircleOutline, IoAlertCircleOutline, IoMailUnreadOutline, IoTrashOutline } from "react-icons/io5";
import { Tooltip } from "@mui/joy";
import { ClosedViewID, DeletedViewID, InboxViewID, OpenViewID, UnresolvedViewID, WithUserViewID } from "@/Store/Reducers/cache/HardCodedViews";
import { useCache } from "@/Hooks/useCache";
import { useAgent } from "@/Hooks/useAgent";
import api from "@/Api/Api";
import ViewButton from "./ViewGroup/ViewButton/ViewButton";
import ViewDropdown from "./ViewGroup/ViewDropdown/ViewDropdown";
import ViewGroup from "./ViewGroup/ViewGroup";
import Header from "./Header/Header";
import { ViewGroupOptions } from "./Header/SettingButton/ContextMenu";
import { useAppDispatch, useAppSelector } from "../../../Store/hooks";
import { customFilterOpened, customViewCreated, viewChanged } from "../../../Store/Reducers/ticketsPageSlice";
import { AgentAvatar } from "../../../Components/Avatar/AgentAvatar";
import { View } from "@shared/Models/View";
import { TicketCounts } from "@shared/Entities/EntityTypes";

export function TicketsViewList() {
	const dispatch = useAppDispatch();
	const viewId = useAppSelector(state => state.ticketsPage.viewId);
	const [ticketCounts, setTicketCounts] = useState<TicketCounts | undefined>();
	const agent = useAgent();

	const scrollContainerRef = useRef<HTMLDivElement>(null!);
	const [scrolled, setScrolled] = useState(false);

	const [viewGroups, setViewGroups] = useState<ViewGroupOptions[]>(loadActiveViewGroups);

	function loadActiveViewGroups(): ViewGroupOptions[] {
		const localData = localStorage.getItem("viewGroups");
		const userVGs: ViewGroupOptions[] = localData != null ? JSON.parse(localData) : [];

		const defaultVGs = [
			{ name: "Teams", on: true },
			{ name: "Agents", on: true },
			{ name: "Utility", on: true }
		];

		// Filter out overriden keys from default VGs.
		const filteredDefaultVGs = defaultVGs.filter(value => userVGs.findIndex(subValue => subValue.name == value.name) == -1);

		// Filter out deleted keys from user VGs.
		const filteredUserVGs = userVGs.filter(value => defaultVGs.find(subValue => subValue.name == value.name) != undefined);

		// Merge and sort.
		return [...filteredDefaultVGs, ...filteredUserVGs];
	}

	const cache = useCache();

	// Casting these to Views as they are hardcoded so we know they exist.
	const inboxView = cache.getView(InboxViewID) as View;
	const unresolvedView = cache.getView(UnresolvedViewID) as View;
	const deletedView = cache.getView(DeletedViewID) as View;

	const openView = cache.getView(OpenViewID) as View;
	const withUserView = cache.getView(WithUserViewID) as View;
	const closedView = cache.getView(ClosedViewID) as View;

	const customViews = cache.Views.filter(view => view.id != null && typeof view.id != "string" && view.id > 0);

	useEffect(() => {
		async function doFetch() {
			const response = await api.BasicGet<TicketCounts>("/ticketcounts");

			if (response?.data != null) {
				setTicketCounts(response.data);
			}
		}

		doFetch();

		const interval = setInterval(() => {
			doFetch();
		}, 30000);

		return () => clearInterval(interval);
	}, []);

	// Keep viewGroups persisted to localStorage.
	useEffect(() => {
		localStorage.setItem("viewGroups", JSON.stringify(viewGroups));
	}, [viewGroups]);

	// For adding border top on scroll
	const handleScroll = () => {
		if (scrollContainerRef.current?.scrollTop > 0) {
			setScrolled(true);
		} else {
			setScrolled(false);
		}
	};

	function setActiveView(view: View) {
		dispatch(viewChanged(view));
	}

	function newViewCreated() {
		dispatch(customViewCreated());

		dispatch(customFilterOpened({
			deleted: { type: "bool", comparison: "equals", value: false },
		}));
	}

	function getCustomViewTicketCount(viewId: number) {
		return ticketCounts?.viewCounts[viewId];
	}

	return (
		<div className="flex w-80 flex-shrink-0 flex-col border-r border-gray-300 bg-white" >
			<div className={"flex flex-col pt-4 px-3"} >
				<Header setViewGroups={setViewGroups} viewGroups={viewGroups} />

				<div className="flex flex-col gap-1" style={{ scrollbarGutter: "auto" }}>
					<Tooltip title="Open ticket that are assigned to you" placement="bottom" disableInteractive enterDelay={500}>
						<div> {/* Tooltip needs this div for some reason. */}
							<ViewButton
								active={viewId == InboxViewID}
								view={inboxView}
								count={ticketCounts?.mine}
								icon={<IoMailUnreadOutline size={18} />}
								setActiveView={setActiveView}
							/>
						</div>
					</Tooltip>

					<Tooltip title="All unclosed tickets" placement="bottom" disableInteractive enterDelay={500}>
						<div> {/* Tooltip needs this div for some reason. */}
							<ViewButton
								active={viewId == UnresolvedViewID}
								view={unresolvedView}
								count={ticketCounts?.unresolved}
								icon={<IoAlertCircleOutline size={18} />}
								setActiveView={setActiveView}
							/>
						</div>
					</Tooltip>

					{customViews.map(view =>
						<ViewButton
							key={view.id}
							active={viewId == view.id}
							view={view}
							icon={view.icon ?? ""}
							setActiveView={setActiveView}
							count={getCustomViewTicketCount(view.id as number)}
							hasContextMenu
						/>
					)}

					<ViewButton
						active={viewId == DeletedViewID}
						view={deletedView}
						icon={<IoTrashOutline size={18} />}
						setActiveView={setActiveView}
					/>

					<span
						className="view-button relative z-[1] flex h-9 w-full select-none items-center rounded-md px-1 py-2 text-left leading-4 text-gray-500 hover:bg-gray-100 cursor-pointer border border-transparent"
						onClick={newViewCreated}
					>
						<IoAddCircleOutline size={18} className="w-5 mr-2 flex items-center justify-center" />New View
					</span>
				</div>
			</div>

			<div
				className={
					"h-full overflow-y-auto border-t px-3 pb-4 flex flex-col gap-1 " +
					(scrolled ? " border-gray-300 " : " border-transparent ")
				}
				style={{ scrollbarGutter: "stable" }}
				onScroll={handleScroll}
				ref={scrollContainerRef}
			>
				<AnimatePresence>
					{viewGroups.map((viewGroup) => (
						<motion.div
							layout
							key={viewGroup.name}
						>
							{viewGroup.on && (
								<ViewGroup name={viewGroup.name}>
									{viewGroup.name == "Utility" && (
										<>
											<ViewButton
												key={OpenViewID}
												active={viewId == OpenViewID}
												view={openView}
												setActiveView={setActiveView}
												count={ticketCounts?.statusOpen}
											/>
											<ViewButton
												key={WithUserViewID}
												active={viewId == WithUserViewID}
												view={withUserView}
												setActiveView={setActiveView}
												count={ticketCounts?.statusUser}
											/>
											<ViewButton
												key={ClosedViewID}
												active={viewId == ClosedViewID}
												view={closedView}
												setActiveView={setActiveView}
												count={ticketCounts?.statusClosed}
											/>
										</>
									)}

									{viewGroup.name == "Agents" && (
										<>
											{cache.Agents.map(agent => { // Try to output a dropdown for each team.
												const view = cache.Views.find(view => view.id == "agentId:" + agent.id);

												if (view != null) {
													return <ViewButton
														key={agent.id}
														active={viewId == view.id}
														view={view}
														icon={<AgentAvatar agent={agent} widthPx={20} hideAgentStatus />}
														setActiveView={setActiveView}
														count={ticketCounts?.agentCounts[agent.id]}
													       />;
												} else {
													return undefined;
												}
											})}
										</>
									)}
									{viewGroup.name == "Teams" && (
										<>
											{cache.Teams.map(team => { // Try to output a dropdown for each team.
												const agentInTeam = agent?.teams?.some(agentTeam => agentTeam.teamId == team.id);

												if (agent != null && !agent.isAdmin && !agentInTeam) {
													return;
												}

												const views = cache.Views.filter(view => view.groupName == "Teams" && view.subGroupId == team.id);

												// Used to remove the gutter for icons if no team has one.
												const anyTeamHasAnIcon = cache.Teams.filter(team => team.icon != null && team.icon != "").length > 0;

												return <ViewDropdown
													key={team.id}
													icon={team.icon != null && team.icon != "" ? team.icon : anyTeamHasAnIcon ? "" : undefined}
													name={team.name ?? ""}
													groupId={"teamId:" + team.id + ",agentId:-2"}
													views={views}
													ticketCounts={ticketCounts}
													activeViewId={viewId}
													setActiveView={setActiveView}
												       />;
											})}
										</>
									)}
								</ViewGroup>
							)}
						</motion.div>
					))}
				</AnimatePresence>
			</div>
		</div>
	);
}
